import { useLazyQuery } from "@apollo/client";
import { getHashParams } from "@neonumy/ant-design";
import { API, clientNeoAuth } from "@neonumy/api-clients";
import i18n from "config/i18n/i18n";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { CHECK_USER_AUTHENTICATED } from "queries/neoAuth/neoauth";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onErrorGraphql } from "utils/onErrorGraphql";
import Base from "views/Base";
import useAuthStore from "views/hooks/useAuthStore";

const ACCESS_PERMISSION = "neofact.can-access";

export default function PrivateRoute(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();

  const setUserData = useAuthStore(state => state.setUserData);
  const userData = useAuthStore(state => state.userData);

  const relativeUrlPath = useRef("");
  const relativePathName = `${location.pathname}${location.search}`;

  const [getUserData, { data: getUserData_data, error: getUserDataError }] =
    useLazyQuery<GetUserData>(CHECK_USER_AUTHENTICATED, {
      client: clientNeoAuth,
    });

  useEffect(() => {
    if (getUserDataError) onErrorGraphql(getUserDataError);
  }, [getUserDataError]);

  useEffect(() => {
    if (!getUserData_data) return;

    const getUserByToken = getUserData_data.getUserByToken;

    if (getUserByToken) {
      const canAccess = getUserByToken.permissions
        ? getUserByToken.permissions.findIndex(permission => permission === ACCESS_PERMISSION) > -1
        : false;

      if (!canAccess) {
        navigate("/no-access");
      } else {
        setUserData(getUserByToken);
        i18n.changeLanguage(getUserByToken.language.toLowerCase());
        localStorage.setItem("language", getUserByToken.language.toLowerCase());
        navigate(relativeUrlPath.current, { replace: true });
      }
    } else {
      window.location.href = API.fullLoginUrl;
    }
  }, [getUserData_data]);

  const handleLoginUseEffect = async () => {
    relativeUrlPath.current = relativePathName;

    if (location.hash) {
      const expireSeconds = getHashParams(location, "expires_in");
      const expireDate = dayjs().add(Number(expireSeconds) * 1000, "ms");
      const accessTokenValue = getHashParams(location, "access_token");

      Cookies.set("access_token", accessTokenValue, {
        path: "/",
        expires: expireDate.toDate(),
      });
      Cookies.set("expireDate", expireDate.toISOString(), {
        path: "/",
        expires: expireDate.toDate(),
      });
    }

    if (location.search) {
      const urlParams = new URLSearchParams(location.search);
      const relativeUrlPathParam = urlParams.get("relative_url_path");
      if (relativeUrlPathParam) {
        relativeUrlPath.current = relativeUrlPathParam;
      }
    }

    const cookieAccessToken = Cookies.get("access_token");
    if (cookieAccessToken && !userData) {
      await getUserData();
    } else {
      window.location.href = API.fullLoginUrl;
      return;
    }
  };

  useEffect(() => {
    handleLoginUseEffect();
  }, []);

  return <>{userData && <Base />}</>;
}
